import { EMAIL_REGEX } from "@/utils/regex";

type Maybe<T> = T | null | undefined;

/**
 * A validator returns `undefined` when valid, otherwise an error string.
 * Use a bivariant parameter type so narrower validators can be passed to combinators.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type Validator<Args extends any[] = any[]> = {
  bivarianceHack(...args: Args): string | undefined;
}["bivarianceHack"];

/* ---------- Primitive validators ---------- */

export const validateLength =
  (message: string, length: number): Validator<[Maybe<{ length: number }>]> =>
  (value) =>
    value && value.length >= length ? undefined : message;

export const validateMatches =
  (message: string, regExp: RegExp): Validator<[Maybe<string>]> =>
  (value) =>
    value != null && regExp.test(value) ? undefined : message;

export const validateRequired =
  (message: string): Validator<[unknown]> =>
  (value) =>
    value ? undefined : message;

export const validateRequiredArray =
  (message: string): Validator<[unknown]> =>
  (value) =>
    !Array.isArray(value) || value.length === 0 ? message : undefined;

export const validateEmail =
  (message: string): Validator<[Maybe<string>]> =>
  (value) =>
    validateMatches(message, EMAIL_REGEX)(value);

export const keywordMinLengthWithWildcard =
  (message: string): Validator<[Maybe<string>]> =>
  (value) => {
    if (!value) return undefined;
    if (value.includes("*") && value.length < 4) {
      return message;
    }
    return undefined;
  };

export const keywordMinLengthWithoutWildcard =
  (message: string): Validator<[Maybe<string>]> =>
  (value) => {
    if (!value) return undefined;
    return value.length < 2 ? message : undefined;
  };

/* ---------- Combinators ---------- */

/** Combine validators in sequence, short-circuiting on first error. */
export function sequenceValidators(...validators: Validator[]): Validator {
  return (...value: unknown[]) => {
    for (const validator of validators) {
      const result = validator(...(value as unknown[]));
      if (result) return result;
    }
    return undefined;
  };
}

/** Run all validators and return an array of errors (or undefined if none). */
export function combineValidators(
  ...validators: Validator[]
): (...value: unknown[]) => string[] | undefined {
  return (...value: unknown[]) => {
    const errors: string[] = [];
    for (const validator of validators) {
      const error = validator(...(value as unknown[]));
      if (error) errors.push(error);
    }
    return errors.length ? errors : undefined;
  };
}

/** Lift a single-value validator to work over an array of values. */
export const toArrayValidator =
  <T>(validator: Validator<[T]>): Validator<[Maybe<T[]>]> =>
  (values) => {
    if (!values) return undefined;
    for (const value of values) {
      const result = validator(value);
      if (result) return `${String(value)}: ${result}`;
    }
    return undefined;
  };
